// deps
import React, { ReactElement, useContext, memo, useMemo, useCallback } from "react";

// internal deps
import { PaymentAlert, PaymentMethod, PaymentMethodMeta } from "@swiggy-private/payment-ui";
import { format, useEffectOnce } from "@swiggy-private/core-ui";
import { PaymentFallbacks, PaymentAlertActionProps, OnPayExtraParams } from "@swiggy-private/payment-types";
import {
    CheckoutContext,
    CheckoutActionTypes,
    AnalyticsContext,
    FailedOrderActionPayload,
} from "@swiggy-private/checkout-core";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

// constants
import { DEFAULT_PAYMENT_FALLBACK_DATA } from "@payment-client/constants/PaymentFallback";
import * as TextDict from "@payment-client/constants/TextDict";

// helpers
import { getPaymentFallbackActions } from "@payment-client/helpers/PaymentFallbackHelper";
import { getPaymentFailedAlertSubInfo, getPaymentsCheckoutState } from "@payment-client/helpers/PaymentHelper";
import { isInAppUpiMethodInOrderData } from "@payment-client/helpers/InAppUpiHelper";

// analytics
import { paymentFailedDialogScreenViewEvent } from "@payment-client/analytics/events/PaymentFailed";
export interface PaymentFailedAlertProps {
    payAction: (paymentMethodData: PaymentMethod<PaymentMethodMeta>, _extraParams?: OnPayExtraParams) => void;
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"];
    amountToPay: string;
    itemQuantity: number;
    payload: FailedOrderActionPayload;
}

function PaymentFailedAlert(props: PaymentFailedAlertProps): ReactElement | null {
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { screenViewEvent, clickEvent } = useContext(AnalyticsContext);

    const { placeOrderData, failedOrder, paymentFailureResponse } = props.payload;
    const fallbackData: PaymentFallbacks = DEFAULT_PAYMENT_FALLBACK_DATA;
    const { failureMessage, failureTitle, paymentMethods } = fallbackData;

    const info = format(TextDict.PAYMENT_ALERT_MAIN_TEXT, props.amountToPay);
    const { payAction, paymentGroups, amountToPay } = props;

    const subInfo = useMemo(() => getPaymentFailedAlertSubInfo(props.itemQuantity), [props.itemQuantity]);

    // Handles failure alert action click based on fallback payment code
    const onOverlayClose = useCallback(() => {
        // Handled Swiggy Money & Regular Payments Checkout State
        const checkoutState = getPaymentsCheckoutState();
        setCheckoutState({
            type: checkoutState,
            payload: {
                reload: isInAppUpiMethodInOrderData(props?.payload?.failedOrder),
            },
        });
    }, [props?.payload, setCheckoutState]);

    const fallbackActions: PaymentAlertActionProps[] = useMemo(
        () =>
            getPaymentFallbackActions({
                clickEvent,
                payAction,
                placeOrderData,
                failedOrder,
                setCheckoutState,
                amountToPay,
                paymentGroups,
                fallbackMethods: paymentMethods,
            }),
        [
            amountToPay,
            clickEvent,
            failedOrder,
            payAction,
            paymentGroups,
            paymentMethods,
            placeOrderData,
            setCheckoutState,
        ],
    );

    useEffectOnce(() => {
        // send screen view event
        paymentFailedDialogScreenViewEvent(screenViewEvent);
    });

    if (checkoutState.type !== CheckoutActionTypes.PAYMENT_FAILED) {
        return null;
    }

    return (
        <PaymentAlert
            icon={TextDict.ICON_PAYMENT_FAILED_V4}
            title={paymentFailureResponse?.failureReason || failureTitle}
            message={failureMessage}
            info={info}
            subInfo={subInfo}
            primaryAction={fallbackActions[0]}
            secondaryAction={fallbackActions[1]}
            hideOnOverlayClick={true}
            onClose={onOverlayClose}
        />
    );
}

export default memo(PaymentFailedAlert);
